<template>
  <v-card flat>
    <v-card-title>
      <span class="subtitle-2">Callsheet analyses</span>
      <v-spacer></v-spacer>
      <v-btn icon :href="artworkLink" target="_blank">
        <v-icon small>{{ getIcon('showinbrowser') }}</v-icon>
      </v-btn>
      <v-btn icon @click="close">
        <v-icon :color="showIconColor(true)">{{ showIcon(true) }}</v-icon>
      </v-btn>
    </v-card-title>

    <v-container>
      <v-row>
        <tool-chip-count
          v-if="showChipCount"
          :count="callsheetAnalysesCount"
          :tooltip="`${callsheetAnalysesCount} items discovered`"
          right
        ></tool-chip-count>
        <span class="subtitle-2 pl-2">
          {{ callsheetComment }}
          ({{ callsheetName }})
        </span>
        <v-spacer></v-spacer>
        <fid-chip :itemReason="reason"></fid-chip>
      </v-row>
      <v-row v-if="metadata">
        <span class="ml-4 mt-4 mb-2">Callsheet load metadata</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="flipMeta">
          <v-icon :color="showIconColor(showMeta)">{{
            showIcon(showMeta)
          }}</v-icon>
        </v-btn>
      </v-row>
      <callsheet-metadata
        :callsheet="callsheet"
        v-if="showMeta"
      ></callsheet-metadata>

      <v-row v-if="callsheetAnalyses">
        <span class="ml-4 mt-4 mb-2">Callsheet analysis results</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="flipResults">
          <v-icon :color="showIconColor(showResults)">{{
            showIcon(showResults)
          }}</v-icon>
        </v-btn>
      </v-row>
      <v-row v-if="callsheetAnalyses">
        <v-data-table
          v-if="showResults"
          dense
          :headers="callsheetAnalysesHeaders"
          :items="callsheetAnalyses"
          hide-default-footer
          :no-data-text="'no analyses yet for this callsheet'"
          :items-per-page="callsheetAnalysesCount || 10"
        >
          <template v-slot:item="{ item, headers }">
            <callsheet-analysis-row
              :reason="reason"
              :item="item"
              :headers="headers"
              :expanded-row="expandedRow"
              @expand="expandRow"
            ></callsheet-analysis-row>
          </template>
        </v-data-table>
      </v-row>
    </v-container>

    <v-card-actions>
      <v-spacer></v-spacer>
      <span>Close {{ callsheetName }}</span>
      <v-btn icon @click="close">
        <v-icon :color="showIconColor(true)">{{ showIcon(true) }}</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
/**
 * this is called by callsheets to show the detail of a given callsheet
 * the callsheet detail arrives as a prop
 * and this component does a query on callsheetanalyses to get rows
 *
 * its kind of a maze from here, so here's the component heirachy
 * callsheetAnalyses [..]  :1 row per loaded callsheet
 *   callsheetAnalysisRow  :a row of name/role/type
 *     callsheetAnalysisExpand :row can expand to allow selection of analysis detail/kraph stuff/officially assign person
 *       callsheetAnalysisExpandDetail :what the analysis discovered
 *       kgraphPanel :any google knowledggraph stuff
 *       callsheetAnalysisAssign :expands to a list of known people who this gues person could be
 *         callsheetAnalysisAssignRow :expands to a dialog to update or assign as someone else or another role
 *           tablePersonToFilms :a set of known film to roles for a selected person's row
 *             tablePersonToFilmsDialog :a dialog to add to or update person to films
 *
 */
import { CallsheetAnalyses } from "@/gql";
import CallsheetAnalysisRow from "@/components/Callsheetanalysisrow";
import CallsheetMetadata from "@/components/Callsheetmetadata";
import { getIconColor, getIcon } from "@/js/store";
import { mapState, mapMutations } from "vuex";
import { vxMaps } from "@/js/vxstore";
import { SearchUtils } from "@/js";
import Fidchip from "@/components/Fidchip";
import Toolchipcount from "@/components/Toolchipcount";

export default {
  props: {
    callsheet: Object,
    reason: Object,
  },
  components: {
    "fid-chip": Fidchip,
    "callsheet-analysis-row": CallsheetAnalysisRow,
    "tool-chip-count": Toolchipcount,
    "callsheet-metadata": CallsheetMetadata,
  },
  methods: {
    // TODO this return to position is not that great as scrolling may have happened too
    // need to work on an element related position rather than window
    // for another day
    expandRow(item) {
      if (!item && this.expandPosition) {
        this.$vuetify.goTo(this.expandPosition);
      } else if (item) {
        this.expandPosition = window.pageYOffset;
      }
      this.expandedRow = item;
    },
    showIcon(showing) {
      return getIcon(showing ? "hide" : "show");
    },
    showIconColor(showing) {
      return getIconColor(showing ? "hide" : "show");
    },
    getIcon(name) {
      return getIcon(name);
    },
    getIconColor(name) {
      return getIconColor(name);
    },
    close() {
      this.$emit("close");
    },
    flipMeta() {
      this.showMeta = !this.showMeta;
    },
    flipResults() {
      this.showResults = !this.showResults;
    },
    ...mapMutations(vxMaps.mutations),
  },
  data: () => ({
    showResults: false,
    showMeta: false,
    CallsheetAnalyses: null,
    runID: SearchUtils.getRunID(),
    expandedRow: null,
    callsheetAnalysesHeaders: [
      { text: "Name", value: "person" },
      { text: "Role", value: "profession" },
      { text: "Assigned", value: "personID" },
    ],
  }),

  computed: {
    metadata() {
      return this.callsheet && this.callsheet.callsheetMetadata;
    },
    // are there any callsheet analyses available
    isCallsheetAnalyses() {
      return this.callsheet && this.callsheet.CallsheetAnalyses;
    },
    // how many are there
    callsheetAnalysesCount() {
      const isca = this.isCallsheetAnalyses;
      return (isca && isca[0] && isca[0].countRows) || 0;
    },
    showChipCount() {
      return Boolean(this.callsheetAnalysesCount);
    },
    artworkLink() {
      return SearchUtils.getArtworkLink({ base: this.callsheet });
    },
    callsheetComment() {
      return this.callsheet && this.callsheet.comment;
    },
    callsheetName() {
      return this.callsheet && this.callsheet.originalName;
    },
    callsheetID() {
      return this.callsheet && this.callsheet.id;
    },
    callsheetAnalyses() {
      return this.CallsheetAnalyses;
    },
    labelColor() {
      return getIconColor("labels");
    },
    xs() {
      return this.$vuetify.breakpoint.xs;
    },
    errorIcon() {
      return getIcon("error");
    },
    ...mapState(vxMaps.state),
  },
  apollo: {
    CallsheetAnalyses: {
      skip() {
        return !this.callsheetID || !this.callsheetAnalysesCount;
      },
      query: CallsheetAnalyses,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          id: this.callsheetID,
          limit: this.callsheetAnalysesCount,
        };
      },
    },
  },
};
</script>
